import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiX } from "react-icons/fi";
import { saveConsent, getStoredConsent } from "../../../utils/cookieConsent.js";

/**
 * Modale « Paramètres des cookies ».
 * Permet de personnaliser les catégories de cookies acceptés.
 */
export default function CookieSettingsModal({ open, onClose }) {
  const { t } = useTranslation();
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (open) {
      const current = getStoredConsent();
      setAnalytics(current?.analytics ?? false);
      setMarketing(current?.marketing ?? false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const handleSave = () => {
    saveConsent({ analytics, marketing });
    onClose();
  };

  const handleAcceptAll = () => {
    saveConsent({ analytics: true, marketing: true });
    setAnalytics(true);
    setMarketing(true);
    onClose();
  };

  const handleRejectAll = () => {
    saveConsent({ analytics: false, marketing: false });
    setAnalytics(false);
    setMarketing(false);
    onClose();
  };

  if (!open) return null;

  const Toggle = ({ checked, onChange, disabled }) => (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition ${
        checked ? "bg-[#976DD0]" : "bg-gray-300"
      } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
          checked ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );

  const Category = ({ title, desc, checked, onChange, disabled }) => (
    <div className="flex items-start justify-between gap-4 border-b border-gray-100 py-4">
      <div className="pr-4">
        <p className="text-[15px] font-semibold text-gray-900">{title}</p>
        <p className="mt-1 text-[13px] leading-relaxed text-gray-600">{desc}</p>
      </div>
      <div className="shrink-0 pt-1">
        <Toggle checked={checked} onChange={onChange} disabled={disabled} />
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-xl max-h-[85vh] flex flex-col">
        <div className="flex items-start justify-between border-b border-gray-100 px-6 py-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              {t("cookieConsent.settingsTitle")}
            </h3>
            <p className="mt-1 text-[13px] text-gray-600">
              {t("cookieConsent.settingsSubtitle")}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
            aria-label={t("cookieConsent.close")}
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6">
          <Category
            title={t("cookieConsent.necessaryTitle")}
            desc={t("cookieConsent.necessaryDesc")}
            checked
            disabled
          />
          <Category
            title={t("cookieConsent.analyticsTitle")}
            desc={t("cookieConsent.analyticsDesc")}
            checked={analytics}
            onChange={setAnalytics}
          />
          <Category
            title={t("cookieConsent.marketingTitle")}
            desc={t("cookieConsent.marketingDesc")}
            checked={marketing}
            onChange={setMarketing}
          />
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-gray-100 px-6 py-4">
          <div className="flex gap-2">
            <button
              onClick={handleRejectAll}
              className="text-[13px] font-medium text-gray-600 hover:text-gray-900 px-3 py-2"
            >
              {t("cookieConsent.rejectAll")}
            </button>
            <button
              onClick={handleAcceptAll}
              className="text-[13px] font-medium px-3 py-2 text-[#976DD0] hover:bg-[#F6F0FB] rounded-full"
            >
              {t("cookieConsent.acceptAll")}
            </button>
          </div>
          <button
            onClick={handleSave}
            className="inline-flex items-center justify-center rounded-full bg-[#976DD0] px-6 py-2.5 text-[14px] font-semibold text-white transition hover:bg-[#8557C0]"
          >
            {t("cookieConsent.saveChoices")}
          </button>
        </div>
      </div>
    </div>
  );
}